import { useState, type ReactNode } from 'react';
import type { CountryDto } from '../api/types';
import { useCountry } from './company';

/**
 * Ayuda de autogestión de cada pantalla: qué hace, cómo se usa paso a paso y qué revisar. Se abre sola la primera vez
 * que el usuario entra a la pantalla en este navegador; después queda plegada y se abre con un clic.
 */
interface HelpContent {
  title: string;
  what: string;
  steps: string[];
  tips?: string[];
}

type HelpBuilder = (country: CountryDto | null) => HelpContent;

const ss = (c: CountryDto | null) => c?.socialSecurity ?? 'la seguridad social';
const bonus = (c: CountryDto | null) => c?.bonusName ?? 'el aguinaldo';
const tax = (c: CountryDto | null) => c?.incomeTax ?? 'el impuesto sobre la renta';

export const HELP: Record<string, HelpBuilder> = {
  dashboard: () => ({
    title: 'Resumen contable',
    what: 'Muestra de un vistazo cómo está la contabilidad de su empresa: resultado del mes, saldos de clientes, proveedores y bancos, y los últimos asientos.',
    steps: [
      'Revise los avisos en amarillo: indican asientos abiertos o descuadrados que debe atender.',
      'Haga clic en un asiento de la lista para ver su detalle.',
      'Use el menú de la izquierda para ir a cada módulo.',
    ],
    tips: ['Las cifras incluyen sólo asientos aplicados. Un asiento abierto no afecta saldos hasta que se aplica.'],
  }),
  entries: () => ({
    title: 'Asientos contables',
    what: 'Lista todos los asientos de la empresa: los que usted registra a mano y los que generan automáticamente facturación, cobros, caja, bancos y planilla.',
    steps: [
      'Filtre por fechas, estado o texto para encontrar un asiento.',
      'Use "Nuevo asiento" para registrar un movimiento manual (ajustes, reclasificaciones, aperturas).',
      'Abra un asiento para ver sus líneas, aplicarlo o anularlo.',
    ],
    tips: [
      'Un asiento aplicado no se borra: se anula y el sistema registra el reverso exacto.',
      'Los asientos automáticos se anulan desde su módulo (factura, cobro, planilla…), no desde aquí.',
    ],
  }),
  entryEditor: () => ({
    title: 'Asiento manual',
    what: 'Registra un asiento con las cuentas, débitos y créditos que usted indique.',
    steps: [
      'Indique la fecha y una descripción clara (se verá en los reportes).',
      'Agregue las líneas: cuenta, débito o crédito y monto. Sólo se permiten cuentas de detalle activas.',
      'Verifique que el total de débitos sea igual al de créditos.',
      'Guárdelo abierto para revisarlo después, o marque "Aplicar al guardar" para que afecte los saldos.',
    ],
    tips: ['La fecha debe caer en un período abierto.'],
  }),
  reports: () => ({
    title: 'Reportes contables',
    what: 'Genera el balance de comprobación, libro diario, libro mayor, balance general, estado de resultados, flujo de efectivo, cambios en el patrimonio y el catálogo de cuentas.',
    steps: ['Elija el reporte en las pestañas.', 'Indique el rango de fechas y, si aplica, la moneda o el nivel de cuentas.', 'Revise el resultado en pantalla o use "Exportar CSV" para abrirlo en Excel.'],
    tips: [
      'Si el balance general muestra clasificaciones "sin sección", asígnelas en Configuración → Niveles y clasificaciones.',
      'Los asientos anulados aparecen junto con su reverso, por eso su efecto neto es cero.',
      'Flujo de efectivo: la primera vez use "Configurar cuentas…" para marcar sus cajas y bancos, y asigne inversión o financiamiento a activos fijos y préstamos.',
      'El estado de resultados no incluye el asiento de cierre anual, así el resultado del año se sigue viendo después del cierre.',
    ],
  }),
  receivables: () => ({
    title: 'Cuentas por cobrar',
    what: 'Controla lo que le deben sus clientes: facturas, notas de débito y crédito, cobros, antigüedad de saldos y estado de cuenta.',
    steps: [
      'Las facturas y pagos de su sistema de facturación llegan solos por la integración. Aquí puede consultarlos.',
      'Para un documento manual use los botones Factura, Nota de débito o Nota de crédito; para un pago use "Registrar cobro" y aplíquelo a las facturas.',
      'En "Antigüedad" vea cuánto está vencido y en "Estado de cuenta" el detalle de un cliente.',
    ],
    tips: [
      'Un documento con cobros aplicados no se anula: primero anule los cobros.',
      'Las cuentas contables salen de las plantillas configuradas en Integración.',
    ],
  }),
  payables: () => ({
    title: 'Cuentas por pagar',
    what: 'Controla lo que su empresa debe a proveedores: facturas, pagos, antigüedad y estado de cuenta.',
    steps: [
      'Registre primero el proveedor en la pestaña "Proveedores" (con su tipo de servicio).',
      'Registre la factura del proveedor con sus montos e impuestos. Si su empresa tiene activada la lectura con IA, pulse "Leer factura con foto": tome la foto (o elija una imagen o PDF) y el formulario se llena solo.',
      'Revise cada dato contra la foto, corrija lo que haga falta y pulse Registrar. Si el proveedor no existe, el sistema le ofrece crearlo con el nombre y la identificación leídos.',
      'Registre el pago indicando qué facturas cancela.',
    ],
    tips: [
      'Revise la antigüedad cada semana para programar pagos y evitar recargos.',
      'La foto queda guardada con la factura: use "Ver factura" en la lista. La plantilla que use con un proveedor se sugiere sola en su próxima factura.',
      'La IA puede equivocarse: nada se registra hasta que usted revisa y pulsa Registrar. Lea los avisos amarillos (montos que no cuadran, factura repetida, tarifa distinta).',
    ],
  }),
  banks: () => ({
    title: 'Bancos',
    what: 'Maneja sus cuentas bancarias: depósitos, retiros, transferencias, cheques y conciliación con el estado de cuenta del banco.',
    steps: [
      'Registre el banco y luego cada cuenta bancaria con su cuenta contable.',
      'Registre depósitos, retiros y transferencias: cada uno genera su asiento.',
      'En "Conciliación" pegue las líneas del estado de cuenta del banco y pulse Conciliar: el sistema empareja los movimientos y le muestra los pendientes.',
    ],
    tips: ['Concilie al cierre de cada mes antes de cerrar el período.'],
  }),
  cash: () => ({
    title: 'Cajas',
    what: 'Controla el efectivo de las cajas del punto de venta: apertura con fondo, ingresos, egresos, arqueo y cierre.',
    steps: [
      'Asigne en "Usuarios" quién puede operar cada caja.',
      'Abra la caja con el fondo inicial al empezar el día.',
      'Registre ingresos y egresos que no sean cobros a clientes.',
      'Al terminar, haga el arqueo y cierre con el efectivo contado; si hay diferencia, se registra el faltante o sobrante.',
    ],
    tips: ['Los cobros en efectivo que llegan de la facturación se suman solos al arqueo.'],
  }),
  payroll: (c) => ({
    title: 'Planilla',
    what: `Calcula y contabiliza los salarios de su personal según las leyes de ${c?.name ?? 'su país'}: cargas de ${ss(c)}, ${tax(c).toLowerCase()}, horas extra, ${bonus(c).toLowerCase()}, vacaciones y liquidaciones.`,
    steps: [
      'Antes de la primera planilla revise "Parámetros legales" (menú Planilla): ahí están los porcentajes y tablas de su país.',
      'Registre a cada empleado con sus horas del período y su salario por hora.',
      'Cree una planilla (grupo) con su frecuencia de pago y agregue los empleados.',
      'Registre las novedades del período: horas extra, incapacidades y vacaciones.',
      'En "Generar planilla" elija la planilla y el período, revise la vista previa y genere: se crea el asiento.',
    ],
    tips: [
      'Una planilla generada no se edita: si hay un error, anúlela (se reversa el asiento) y vuelva a generarla.',
      `${bonus(c)} se genera desde la pestaña "Bonos de ley" con los salarios ya pagados.`,
      'Para liquidar a un empleado, primero inactívelo con su fecha de salida. Las cargas de la liquidación salen de "Otros parámetros"; la renta a retener la indica usted.',
      `En "Reporte a la seguridad social" obtiene cada mes el detalle por empleado para ${ss(c)}. Registre el número de seguro social de cada empleado.`,
    ],
  }),
  payrollLegal: (c) => ({
    title: `Parámetros legales de planilla — ${c?.name ?? 'su país'}`,
    what: `Aquí están las reglas de ley con que se calcula su planilla: cargas de ${ss(c)}, tabla de ${tax(c).toLowerCase()}, ${bonus(c).toLowerCase()}, vacaciones, cesantía, preaviso y recargos de horas extra. Su empresa las administra: puede revisarlas y ajustarlas cuando cambie la ley.`,
    steps: [
      'La primera vez el sistema carga los valores vigentes de su país. Use "Cargar valores del país" si falta algo; nunca reemplaza lo que usted ya cambió.',
      'Revise los puntos marcados "Revisar": son valores donde las fuentes no coinciden o que dependen de su actividad. Confírmelos con su contador y guárdelos.',
      'Cuando cambie una tasa por ley, no edite la anterior: póngale fecha final y cree la nueva con su fecha de inicio. Así las planillas viejas conservan su cálculo.',
      'Para la renta, cree una tabla nueva con la fecha de inicio del nuevo año.',
    ],
    tips: [
      'Las planillas ya generadas guardan la tasa que usaron; cambiar un valor no las modifica.',
      'Si una carga tiene código, su asiento puede llevarla a su propia cuenta (campo "Carga: …" en la plantilla de planilla).',
    ],
  }),
  accounts: () => ({
    title: 'Catálogo de cuentas',
    what: 'Define las cuentas contables de su empresa en forma de árbol: cuentas madre que agrupan y cuentas de detalle que reciben movimientos.',
    steps: [
      'Cree primero las cuentas madre de cada nivel (activos, pasivos, patrimonio, ingresos, gastos).',
      'Cree las cuentas de detalle debajo de su cuenta madre; el número se arma según los niveles configurados.',
      'Indique si la cuenta es deudora o acreedora y su clasificación.',
    ],
    tips: ['Una cuenta con movimientos no se elimina: inactívela.', 'Defina primero los niveles en "Niveles y clasificaciones".'],
  }),
  structure: () => ({
    title: 'Niveles y clasificaciones',
    what: 'Define cuántos niveles tiene su catálogo de cuentas y las clasificaciones que ordenan el balance general y el estado de resultados.',
    steps: ['Cree los niveles en orden (1, 2, 3…) con el máximo de caracteres del número de cuenta en cada uno.', 'Cree las clasificaciones y asigne a cada una su sección del estado financiero.'],
    tips: ['Si un reporte muestra clasificaciones sin sección, asígnelas aquí.'],
  }),
  templates: () => ({
    title: 'Plantillas contables',
    what: 'Las plantillas dicen a qué cuentas va cada asiento automático de Cuentas por cobrar, Cuentas por pagar, Bancos y Planilla. El sistema nunca usa cuentas fijas: siempre salen de aquí.',
    steps: [
      'Cree la plantilla: módulo, descripción y líneas (cuenta, débito o crédito y qué monto lleva cada línea).',
      'En "Plantillas por operación" asigne a cada operación (factura de cliente, depósito, planilla…) la plantilla que debe usar. La columna "Montos" indica qué montos trae cada operación.',
    ],
    tips: [
      'Cada plantilla debe cuadrar: la suma de los montos al débito debe ser igual a la del crédito.',
      'Las plantillas de la facturación, los pagos y las cajas no aparecen aquí: se configuran en Configuración → Integración.',
    ],
  }),
  periods: () => ({
    title: 'Períodos contables',
    what: 'Abre y cierra los meses contables. Sólo se pueden registrar asientos en períodos abiertos.',
    steps: [
      'No hace falta abrir cada mes: cuando se registra el primer movimiento de un mes nuevo, su período se crea abierto solo. También puede crearlo aquí por adelantado.',
      'Al terminar el mes revise la vista previa de cierre: asientos abiertos o descuadrados impiden cerrar.',
      'Cierre el período; para el cierre fiscal registre el mes de cierre de su año fiscal.',
      'En el último mes del año fiscal, haga primero el Cierre anual (traslada la utilidad o pérdida a patrimonio) y después cierre el mes.',
    ],
    tips: [
      'Cierre cada mes cuando haya conciliado bancos y revisado reportes; así nadie modifica meses ya reportados.',
      'Los meses se cierran en orden: no se puede cerrar un mes si el anterior sigue abierto.',
      'El período no se crea solo para un mes anterior a otro ya cerrado, ni para un mes que ya está en un cierre anual, ni para más de un mes en el futuro: en esos casos créelo aquí si de verdad corresponde.',
    ],
  }),
  settings: () => ({
    title: 'Parámetros contables',
    what: 'Define las cuentas que usan los procesos automáticos de su empresa: la cuenta que recibe el resultado del año en el cierre anual las cuentas del diferencial cambiario y la cuenta del inventario inicial.',
    steps: [
      'Elija la cuenta de resultados acumulados: debe ser una cuenta de detalle de patrimonio (por ejemplo "Utilidades acumuladas").',
      'Si maneja saldos en otra moneda, elija la cuenta de ingreso por ganancia cambiaria y la de gasto por pérdida cambiaria.',
      'Si usa la integración de inventario, elija la cuenta del inventario inicial (patrimonio): contra ella se registran las existencias que el inventario envía al empezar.',
      'Pulse Guardar. Los cambios aplican a los procesos que se ejecuten desde ese momento.',
    ],
    tips: ['Si una cuenta no aparece como válida, revise en Niveles y clasificaciones que su clasificación tenga la sección correcta (patrimonio, ingresos o gastos).'],
  }),
  yearEnd: () => ({
    title: 'Cierre anual',
    what: 'Al terminar su año fiscal, registra el asiento que deja en cero todas las cuentas de ingresos, costos y gastos y lleva la utilidad o pérdida del año a la cuenta de resultados acumulados. El Estado de Resultados del año no cambia: no cuenta este asiento.',
    steps: [
      'Aplique todos los asientos del año y revise el Estado de Resultados.',
      'Pulse "Nuevo cierre anual", elija el año y el mes en que termina su año fiscal y pulse Revisar.',
      'Lea los avisos: lo que aparece en rojo impide cerrar (por ejemplo, falta la cuenta en Parámetros contables o hay asientos sin aplicar).',
      'Revise el asiento propuesto y pulse "Registrar cierre anual". Después cierre ese mes en Períodos.',
    ],
    tips: [
      'Si después del cierre necesita registrar algo del año, anule el cierre (se registra su reverso), haga la corrección y vuelva a generarlo. El mes debe seguir abierto.',
      'El primer año fiscal empieza el mes siguiente a su último cierre fiscal registrado; si no hay ninguno, se toman los 12 meses que terminan en el mes elegido.',
    ],
  }),
  security: () => ({
    title: 'Usuarios y roles',
    what: 'Decide qué puede hacer cada usuario de su comercio en la contabilidad: registrar, aplicar, anular, cerrar, configurar o sólo consultar. Los usuarios son los mismos con los que entran al sistema; aquí sólo se les asignan roles.',
    steps: [
      'En la pestaña Roles pulse "Crear roles sugeridos" (o cree los suyos marcando los permisos de cada uno).',
      'En la pestaña Usuarios asígnese primero el rol Administrador a usted mismo.',
      'Asigne a cada usuario el rol que le corresponde. Un usuario puede tener varios roles.',
    ],
    tips: [
      'Mientras no asigne ningún rol, todos los usuarios tienen acceso completo. Desde la primera asignación, un usuario sin rol no entra a la contabilidad.',
      'Siempre debe quedar al menos un usuario con el permiso "Administrar usuarios y roles"; el sistema no permite quitar el último.',
      'Los cambios aplican en menos de un minuto; el usuario puede tener que recargar la página para ver el menú actualizado.',
    ],
  }),
  tax: (c) => ({
    title: `Impuestos (${c?.salesTax ?? 'impuesto de ventas'})`,
    what: `Arma el libro de ventas, el libro de compras y un resumen por tarifa del ${c?.salesTax ?? 'impuesto de ventas'} para preparar su declaración mensual. Las ventas salen de Cuentas por cobrar (incluye lo que envía la facturación) y las compras de Cuentas por pagar.`,
    steps: [
      'La primera vez abra la pestaña Tarifas y pulse "Cargar tarifas del país". Revise las marcadas "Revisar" con su contador.',
      'Elija el mes (desde y hasta) y pulse Consultar.',
      'Revise el libro de ventas: los documentos "Sin desglose" tienen varias tarifas o una tarifa que no está en su lista.',
      'Use el Resumen para llenar la declaración y expórtelo a Excel para su archivo.',
    ],
    tips: [
      'El resumen no presenta la declaración ni incluye retenciones, saldos a favor anteriores ni prorrata: agréguelos usted.',
      'Las notas de crédito restan. Los documentos anulados no aparecen.',
    ],
  }),
  fx: () => ({
    title: 'Diferencial cambiario',
    what: 'Al cierre de cada mes, ajusta al tipo de cambio del día los saldos de las cuentas que están en moneda extranjera (por ejemplo, bancos o clientes en dólares) y registra la diferencia como ganancia o pérdida cambiaria.',
    steps: [
      'Indique en Parámetros contables las cuentas de ganancia y de pérdida cambiaria (una sola vez).',
      'Pulse "Nueva revaluación", elija el último día del mes y la moneda. Deje el tipo de cambio vacío para usar el registrado, o escríbalo.',
      'Revise las cuentas y montos que se ajustan y pulse Registrar. Hágalo antes de cerrar el mes.',
    ],
    tips: [
      'Cada revaluación parte de los saldos ya ajustados por la anterior, así que sólo registra la diferencia nueva.',
      'Si se equivocó, anule la revaluación (se registra su reverso) y hágala de nuevo. Sólo se puede anular la más reciente de cada moneda.',
      'Se revalúan las cuentas de detalle cuya moneda en el catálogo es la moneda elegida.',
    ],
  }),
  fixedAssets: () => ({
    title: 'Activos fijos',
    what: 'Lleva el registro de sus activos (equipo, vehículos, mobiliario…) y registra cada mes su depreciación por línea recta con el asiento correspondiente. También registra las bajas.',
    steps: [
      'En Categorías cree los tipos de activo con su vida útil y sus tres cuentas: la del activo, la de depreciación acumulada y la del gasto por depreciación.',
      'En Activos registre cada activo con su costo, valor residual y el mes en que empieza a depreciarse. Registrar el activo no genera asiento: la compra se registra como siempre.',
      'Cada fin de mes, en Depreciación mensual elija el mes, pulse Revisar y luego Registrar.',
      'Para retirar un activo use "Dar de baja": el valor en libros va a la cuenta que usted elija.',
    ],
    tips: [
      'Si un mes no se registró, el mes siguiente incluye lo pendiente automáticamente.',
      'Para corregir, anule la depreciación más reciente (se registra su reverso) y vuelva a registrarla.',
      'La vida útil fiscal de cada tipo de activo la define la normativa de su país: consúltela con su contador.',
    ],
  }),
  inventory: () => ({
    title: 'Inventario',
    what: 'Lleva a la contabilidad el costo de lo que sale y entra del inventario. El módulo de inventario envía cada movimiento con el costo de los productos, y aquí se genera el asiento con las cuentas de la categoría de cada producto.',
    steps: [
      'En Cuentas por categoría configure, para cada categoría del inventario, su cuenta de inventario, de costo de ventas y de ajustes. Configure también la fila "Por defecto" para los productos sin categoría.',
      'En Parámetros contables indique la cuenta del inventario inicial (patrimonio). Luego, desde el módulo de inventario, envíe una sola vez las existencias actuales: el asiento se genera solo (débito a cada cuenta de inventario, crédito a esa cuenta).',
      'A partir de ahí, cada venta, devolución y ajuste del inventario genera su asiento automáticamente. Consúltelos en Movimientos.',
      'Compare cada mes el saldo contable de cada cuenta de inventario con el valor del inventario (cantidad × costo) del módulo de inventario.',
    ],
    tips: [
      'Venta: débito costo de ventas, crédito inventario. Merma o vencimiento: débito ajustes, crédito inventario. Sobrante de conteo: débito inventario, crédito ajustes.',
      'Si usa esta integración, quite el campo "Costo de ventas" de la plantilla de Factura de facturación; si no, el costo se registraría dos veces.',
      'Las compras se contabilizan con la factura del proveedor en Cuentas por pagar (débito al inventario). Sólo si configura "Compras por facturar", las entradas del inventario generan asiento contra esa cuenta.',
      'Un movimiento se anula desde el módulo de inventario: así inventario y contabilidad quedan iguales.',
    ],
  }),
  pettyCash: () => ({
    title: 'Caja chica',
    what: 'Registra las facturas pequeñas que se pagan en efectivo con un fondo fijo. Las facturas no generan asiento una por una: al liquidar el fondo se registra un solo asiento con todos los gastos.',
    steps: [
      'En Fondos cree el fondo: nombre, responsable, monto, la cuenta contable de la caja chica y, si el impuesto de compras se toma como crédito fiscal, su cuenta.',
      'Entregue el efectivo desde Bancos con un retiro o transferencia: débito a la caja chica y crédito al banco.',
      'En Facturas registre cada factura pequeña: proveedor, descripción, cuenta de gasto, subtotal y tarifa del impuesto.',
      'Cuando el efectivo se esté acabando, pulse "Liquidar…": revise el asiento (gastos e impuesto al débito, caja chica al crédito) y regístrelo.',
      'Reponga el efectivo en Bancos por el total liquidado: débito a la caja chica y crédito al banco. Así la caja chica vuelve a su monto.',
    ],
    tips: [
      'Efectivo disponible = monto del fondo − facturas pendientes. Una factura que lo supere no se acepta: liquide y reponga primero.',
      'Una factura pendiente se puede corregir o anular. Si ya está liquidada, anule la liquidación (se registra el reverso) y sus facturas vuelven a quedar pendientes.',
      'Las facturas liquidadas con impuesto aparecen en el libro de compras de Impuestos si el fondo tiene cuenta de impuesto.',
      'Con la lectura con IA activada, en "Nueva factura" pulse "Leer factura con foto": se llenan proveedor, número, fecha, montos e impuesto, y la cuenta de gasto que usó la última vez con ese proveedor. Revise y guarde.',
      'La foto queda como respaldo de la factura: use "Ver factura" en la lista.',
    ],
  }),
  costCenters: () => ({
    title: 'Centros de costo',
    what: 'Permite separar ingresos y gastos por sucursal, departamento o proyecto y ver el resultado de cada uno.',
    steps: [
      'Cree sus centros de costo (código y nombre).',
      'Al registrar un asiento manual, elija el centro en cada línea de ingreso o gasto. Los activos fijos también pueden tener centro: su depreciación se asigna sola.',
      'En "Resultados por centro" elija las fechas y consulte.',
    ],
    tips: ['Las líneas sin centro aparecen como "Sin centro". Los asientos automáticos de facturación todavía no traen centro.'],
  }),
  budget: () => ({
    title: 'Presupuesto',
    what: 'Registra lo que planea vender y gastar cada mes y lo compara con lo real, cuenta por cuenta.',
    steps: [
      'Pulse "Nuevo presupuesto". Puede partir de lo real de un año anterior con un ajuste en porcentaje.',
      'En "Editar montos" ajuste los montos por mes; "Repartir" divide un total anual en 12 meses.',
      'En "Comparar con lo real" elija los meses (y el centro de costo, si usa) para ver la diferencia y el porcentaje ejecutado.',
    ],
    tips: ['Puede tener varias versiones por año (por ejemplo "Original" y "Ajustado").'],
  }),
  audit: () => ({
    title: 'Bitácora',
    what: 'Muestra quién registró, cambió, aplicó, anuló o cerró cada cosa, cuándo y con qué resultado, incluidas las operaciones que llegan del sistema de facturación y pagos. Los registros no se pueden modificar ni borrar.',
    steps: [
      'Elija el rango de fechas y, si quiere, la pantalla, el origen o el resultado.',
      'Use "Buscar" para encontrar un número de asiento, documento o usuario.',
      'Exporte a Excel para guardar o compartir la consulta.',
    ],
    tips: ['Las consultas (ver reportes o listas) no se registran: sólo las operaciones que cambian datos.', '"Rechazado" significa que el sistema no permitió la operación (por ejemplo, un período cerrado) y no se cambió nada.'],
  }),
  integration: () => ({
    title: 'Integración con facturación, pagos, cajas e inventario',
    what: 'Conecta la facturación, los pagos, las cajas y el inventario con la contabilidad: cada factura, pago, movimiento de caja y anulación genera su asiento automáticamente con las cuentas que usted elija aquí.',
    steps: [
      'Elija la operación (Factura, Pago, Ingreso o Egreso de caja…) y, para cada línea, la cuenta, el campo (qué monto va) y si afecta débito o crédito. Guarde.',
      'Si un tipo de pago necesita otras cuentas (por ejemplo tarjeta), elíjalo en "Tipo de pago" y configure sus líneas.',
      'El inventario usa las cuentas de cada categoría de productos: configúrelas en Inventario → Cuentas por categoría.',
      'La conexión con los otros sistemas ya está hecha: usted sólo elige las cuentas.',
    ],
    tips: [
      'Las anulaciones no necesitan configuración: se registra el reverso exacto del asiento original.',
      'Si el inventario está integrado, no use el campo "Costo de ventas" en la Factura: el costo lo registra el inventario y se duplicaría.',
      'Las plantillas de Cuentas por cobrar, Cuentas por pagar, Bancos y Planilla están en Configuración → Plantillas.',
    ],
  }),
};

function readOpen(key: string): boolean {
  try {
    return window.localStorage.getItem(`acc-help-${key}`) !== 'closed';
  } catch {
    return true;
  }
}

function writeOpen(key: string, open: boolean) {
  try {
    window.localStorage.setItem(`acc-help-${key}`, open ? 'open' : 'closed');
  } catch {
    /* sin almacenamiento: la ayuda simplemente vuelve a abrirse */
  }
}

/** "¿Cómo se usa esta pantalla?": se muestra debajo del encabezado de cada pantalla. */
export function PageHelp({ page, extra }: { page: keyof typeof HELP | string; extra?: ReactNode }) {
  const country = useCountry();
  const [open, setOpen] = useState(() => readOpen(page));
  const build = HELP[page];
  if (!build) return null;
  const h = build(country);
  return (
    <details className="acc-help" open={open} onToggle={(e: { currentTarget: { open: boolean } }) => { setOpen(e.currentTarget.open); writeOpen(page, e.currentTarget.open); }}>
      <summary>¿Cómo se usa esta pantalla?</summary>
      <div className="acc-help-body">
        <p>{h.what}</p>
        <ol>{h.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        {h.tips && h.tips.length > 0 && (
          <ul className="acc-help-tips">{h.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
        )}
        {extra}
      </div>
    </details>
  );
}
